import { ROUTES } from "@/constants/routes";

/**
 * Overview pages that have nested entity routes (segmentId, campaignId, etc.).
 * When switching clients on a nested page, redirect to the corresponding overview.
 */
export const CLIENT_OVERVIEW_PAGES = [
  ROUTES.home,
  ROUTES.campaigns,
  ROUTES.segments,
  ROUTES.users,
  ROUTES.vacancies,
  ROUTES.insights,
  ROUTES.integrations,
  ROUTES.transactions,
  ROUTES.budgetPlanning,
  ROUTES.reporting,
] as const;

/**
 * Routes where the segments after the scope are required parameters (not entity IDs).
 * These segments should be preserved when switching clients instead of being stripped.
 * e.g. budget-planning/2026/4 — year/month are required, not entity sub-routes.
 */
const ROUTES_WITH_REQUIRED_PARAMS: Partial<
  Record<(typeof CLIENT_OVERVIEW_PAGES)[number], number>
> = {
  [ROUTES.budgetPlanning]: 2, // year + month
};

/**
 * Returns true when path has structure /clients/{clientId}/{scope}/{...}
 * where scope is in CLIENT_OVERVIEW_PAGES and there is at least one segment
 * after scope beyond any required parameters (e.g. segmentId, campaignId).
 */
export function isNestedClientPage(
  pathname: string,
  clientId: string,
): boolean {
  const pathParts = pathname.split("/");
  const clientIndex = pathParts.indexOf(clientId);
  if (clientIndex === -1) return false;

  const scope = pathParts[clientIndex + 1] as
    | (typeof CLIENT_OVERVIEW_PAGES)[number]
    | undefined;
  if (!scope || !CLIENT_OVERVIEW_PAGES.includes(scope)) return false;

  const requiredParams = ROUTES_WITH_REQUIRED_PARAMS[scope] ?? 0;
  const segmentsAfterScope = pathParts.length - (clientIndex + 2);

  return segmentsAfterScope > requiredParams;
}

/**
 * Returns the overview page path for the current location when switching clients.
 * For routes with required parameters (e.g. budget-planning/:year/:month), those
 * parameters are preserved. Falls back to the current year/month when not present.
 * Returns ROUTES.home as fallback when not under an overview page (admin client
 * index also redirects to home, so this matches the canonical landing surface).
 */
export function getOverviewPageForPath(
  pathname: string,
  clientId: string,
): string {
  const pathParts = pathname.split("/");
  let scope: (typeof CLIENT_OVERVIEW_PAGES)[number] | undefined;
  let scopeIndex: number;

  const clientIndex = pathParts.indexOf(clientId);
  if (clientIndex !== -1) {
    scopeIndex = clientIndex + 1;
    scope = pathParts[scopeIndex] as (typeof CLIENT_OVERVIEW_PAGES)[number];
  } else {
    const clientsIndex = pathParts.indexOf(ROUTES.clients);
    if (clientsIndex !== -1 && pathParts.length > clientsIndex + 2) {
      scopeIndex = clientsIndex + 2;
      scope = pathParts[scopeIndex] as (typeof CLIENT_OVERVIEW_PAGES)[number];
    } else {
      return ROUTES.home;
    }
  }

  if (!scope || !CLIENT_OVERVIEW_PAGES.includes(scope)) {
    return ROUTES.home;
  }

  const requiredParams = ROUTES_WITH_REQUIRED_PARAMS[scope];
  if (requiredParams) {
    const params = pathParts.slice(scopeIndex + 1, scopeIndex + 1 + requiredParams);

    if (params.length === requiredParams && params.every(Boolean)) {
      return `${scope}/${params.join("/")}`;
    }

    // Fallback: use current date when params are missing
    const now = new Date();
    return `${scope}/${now.getFullYear()}/${now.getMonth() + 1}`;
  }

  return scope;
}
